import React, { forwardRef, useCallback, useMemo } from 'react';
import { MultiSelectInput, type MultiSelectOption } from './MultiSelectInput';
import type { LanguageCode } from '@/types/languageCodes';
import { LANGUAGE_MULTISELECT_OPTIONS } from '@/utils/languageUtils';
import { cn } from '@/utils/cn';

interface LanguagesSelectorProps {
  selectedLanguages: LanguageCode[];
  onLanguagesChange: (languages: LanguageCode[]) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export const LanguagesSelector = forwardRef<HTMLDivElement, LanguagesSelectorProps>(
  ({ selectedLanguages, onLanguagesChange, error, disabled = false, className }, ref) => {
    const languageOptions = useMemo<MultiSelectOption[]>(() => {
      return LANGUAGE_MULTISELECT_OPTIONS.map((option) => ({
        value: option.value,
        label: option.label,
        disabled: false,
      }));
    }, []);

    const handleSelectionChange = useCallback((values: string[]) => {
      onLanguagesChange(values as LanguageCode[]);
    }, [onLanguagesChange]);

    return (
      <div ref={ref} className={cn(className)}>
        <MultiSelectInput
          options={languageOptions}
          value={selectedLanguages}
          onChange={handleSelectionChange}
          placeholder="Select languages..."
          dialogTitle="Select Languages"
          searchPlaceholder="Search languages..."
          searchable
          maxVisibleCapsules={4}
          state={error ? 'error' : 'default'}
          error={error}
          disabled={disabled}
          helperText="Patients will see these languages on your public profile"
          fullWidth
          containerClassName="space-y-1"
          suppressMessage
        />
      </div>
    );
  },
);

LanguagesSelector.displayName = 'LanguagesSelector';

export default LanguagesSelector;
